import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Search, Download, ExternalLink, Calendar, User, Hash } from "lucide-react"
import { Pagination } from "@/components/pagination"
import { toast } from "sonner"
import { loadCrawlResults, convertToReportItem, type ReportItem } from "@/lib/crawl-data"

interface DailyReport extends ReportItem {}

const ITEMS_PER_PAGE = 5

export default function DailyReportsPage() {
  const [reports, setReports] = useState<DailyReport[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filterPlatform, setFilterPlatform] = useState<string>("all")
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    async function fetchData() {
      const data = await loadCrawlResults()
      const reports: DailyReport[] = data.results.map((result, index) => 
        convertToReportItem(result, index)
      )
      setReports(reports)
    }
    fetchData()
  }, [])

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.accountUrl.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesPlatform = filterPlatform === "all" || report.platform === filterPlatform
    return matchesSearch && matchesPlatform
  })

  const totalPages = Math.ceil(filteredReports.length / ITEMS_PER_PAGE)
  const paginatedReports = filteredReports.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
      Instagram: "bg-pink-100 text-pink-800",
      Facebook: "bg-blue-100 text-blue-800",
      TikTok: "bg-black text-white",
      Twitter: "bg-sky-100 text-sky-800",
      YouTube: "bg-red-100 text-red-800",
      Website: "bg-gray-100 text-gray-800",
    }
    return colors[platform] || "bg-gray-100 text-gray-800"
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      verified: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    }
    return colors[status] || "bg-gray-100 text-gray-800"
  }

  const handleVerify = (reportId: string, closeDialog?: () => void) => {
    const report = reports.find(r => r.id === reportId)
    toast("Konfirmasi Verifikasi", {
      description: `Apakah Anda yakin ingin memverifikasi laporan dari ${report?.accountName}?`,
      action: {
        label: "OK",
        onClick: () => {
          const updatedReports = reports.map(report => 
            report.id === reportId 
              ? { ...report, status: "verified" as const }
              : report
          )
          setReports(updatedReports)
          
          // Save verified reports to localStorage for Broadcast page
          const verifiedReports = updatedReports.filter(r => r.status === "verified")
          localStorage.setItem("verified_reports", JSON.stringify(verifiedReports))
          
          if (closeDialog) {
            closeDialog()
          }
          toast.success("Report verified successfully", {
            description: "The report has been moved to verification queue",
          })
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {},
      },
    })
  }

  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Daily Reports</h1>
        <p className="text-muted-foreground mt-2">Laporan harian hasil crawling dari semua platform</p>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search account name or URL..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={filterPlatform}
              onChange={(e) => {
                setFilterPlatform(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background hover:bg-muted transition-colors cursor-pointer"
              style={{ color: '#ffffff' }}
            >
              <option value="all" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>All Platforms</option>
              <option value="Website" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>Website</option>
              <option value="Facebook" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>Facebook</option>
              <option value="Instagram" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>Instagram</option>
              <option value="Twitter" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>Twitter</option>
              <option value="TikTok" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>TikTok</option>
              <option value="YouTube" style={{ color: '#ffffff', backgroundColor: '#1a1a1a' }}>YouTube</option>
            </select>
            <Button variant="outline" size="icon">
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <div className="space-y-4">
        {paginatedReports.map((report) => (
          <Card key={report.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge className={getPlatformColor(report.platform)}>{report.platform}</Badge>
                    <Badge className={getStatusColor(report.status)}>
                      {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">{report.detectedAt}</span>
                  </div>

                  <div>
                    <p className="font-semibold text-foreground">{report.accountName}</p>
                    <p className="text-sm text-muted-foreground truncate">{report.accountUrl}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Detected Keywords:</p>
                    <div className="flex flex-wrap gap-2">
                      {report.detectedKeywords.map((keyword, idx) => (
                        <Badge key={idx} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: `${report.confidence * 100}%` }} />
                    </div>
                    <span className="text-sm font-medium">{(report.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Dialog open={selectedReport?.id === report.id && dialogOpen} onOpenChange={(open) => {
                    if (!open) {
                      setSelectedReport(null)
                      setDialogOpen(false)
                    } else {
                      setSelectedReport(report)
                      setDialogOpen(true)
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedReport(report)
                          setDialogOpen(true)
                        }}
                      >
                        View Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      {selectedReport && (
                        <>
                          <DialogHeader>
                            <DialogTitle>{selectedReport.accountName}</DialogTitle>
                            <DialogDescription>{selectedReport.accountUrl}</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="flex items-center gap-2">
                              <Badge className={getPlatformColor(selectedReport.platform)}>{selectedReport.platform}</Badge>
                              <Badge className={getStatusColor(selectedReport.status)}>
                                {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Calendar className="h-4 w-4" />
                                  <span>Detected At</span>
                                </div>
                                <p className="font-medium">{selectedReport.detectedAt}</p>
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Hash className="h-4 w-4" />
                                  <span>Confidence</span>
                                </div>
                                <p className="font-medium">{(selectedReport.confidence * 100).toFixed(1)}%</p>
                              </div>
                            </div>

                            {selectedReport.metadata && (
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm">Metadata</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {selectedReport.metadata.followers && (
                                    <div>
                                      <span className="text-muted-foreground">Followers: </span>
                                      <span className="font-medium">{selectedReport.metadata.followers.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {selectedReport.metadata.posts && (
                                    <div>
                                      <span className="text-muted-foreground">Posts: </span>
                                      <span className="font-medium">{selectedReport.metadata.posts.toLocaleString()}</span>
                                    </div>
                                  )}
                                  {selectedReport.metadata.verified !== undefined && (
                                    <div>
                                      <span className="text-muted-foreground">Verified: </span>
                                      <span className="font-medium">{selectedReport.metadata.verified ? "Yes" : "No"}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="space-y-2">
                              <h4 className="font-semibold text-sm">Detected Keywords</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedReport.detectedKeywords.map((keyword, idx) => (
                                  <Badge key={idx} variant="secondary">
                                    {keyword}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            {selectedReport.content && (
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm">Content</h4>
                                <div className="bg-muted p-3 rounded-lg">
                                  <p className="text-sm">{selectedReport.content}</p>
                                </div>
                              </div>
                            )}

                            <div className="flex gap-2 pt-4 border-t">
                              <Button
                                variant="outline"
                                onClick={() => window.open(selectedReport.accountUrl, '_blank')}
                                className="flex-1"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open URL
                              </Button>
                              {selectedReport.status === "pending" && (
                                <Button
                                  onClick={() => {
                                    handleVerify(selectedReport.id, () => {
                                      setDialogOpen(false)
                                      setSelectedReport(null)
                                    })
                                  }}
                                  className="flex-1 bg-primary hover:bg-primary/90"
                                >
                                  Verify
                                </Button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </DialogContent>
                  </Dialog>
                  {report.status === "pending" && (
                    <Button 
                      size="sm" 
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => handleVerify(report.id)}
                    >
                      Verify
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {paginatedReports.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">No reports found</p>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center py-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  )
}

